import { scaleCursor } from "pages/flyer/builder/drawer/new-tldraw-components/consts";
import { ungroupMaskGroup } from "pages/flyer/builder/drawer/utils/booleanOperations";
import {
  TLShapeId,
  TLUiActionsContextType,
  TLUiToolsContextType,
  Tldraw,
  Editor,
  IndexKey,
  getIndexAbove,
  getIndicesBetween,
  copyAs,
  exportToBlob,
  exportAs,
} from "tldraw";

// Canvas background shapes that are always at the BOTTOM of the z-stack.
// safeSendToBack uses these as the "floor" — user shapes are placed just above them.
const CANVAS_BOTTOM_IDS = new Set<string>(["shape:front", "shape:back", "shape:frontBlackLine", "shape:backBlackLine"]);

// Shapes that bringDieLinesToFront constantly repositions to the TOP of the z-stack.
// These should be invisible to backward/forward operations.
const CANVAS_TOP_IDS = new Set<string>([
  "shape:frontBorderDashLine",
  "shape:backBorderDashLine",
  "shape:frontRedLine",
  "shape:backRedLine",
  "shape:frontFoldLine",
  "shape:backFoldLine",
  "shape:frontLabel",
  "shape:backLabel",
  "shape:diecutLineFront",
  "shape:diecutLineBack",
  "shape:cutLineFront",
  "shape:cutLineBack",
  "shape:a6SafetyLineFront",
  "shape:a6SafetyLineBack",
  "shape:TearawayCutLineFront",
  "shape:TearawayCutLineBack",
  "shape:tearawayFlyerBuilderCutlineFront",
  "shape:tearawayFlyerBuilderCutlineBack",
  "shape:tearawaySafetyLineFront",
  "shape:tearawaySafetyLineBack",
  "shape:addressBox",
  "shape:customMeasure",
]);

// Union of both sets — used to filter ALL infrastructure from backward/forward operations.
const isInfrastructureShape = (id: string) => CANVAS_BOTTOM_IDS.has(id) || CANVAS_TOP_IDS.has(id);

/**
 * Wait until ALL tldraw selection UI elements are removed from the DOM.
 * Enforces a minimum delay so React has time to fully commit the deselection,
 * even if the DOM elements disappear quickly.
 */
function waitForSelectionGone(maxMs = 500, minMs = 150): Promise<void> {
  return new Promise((resolve) => {
    const start = performance.now();
    function check() {
      const fg = document.querySelector(".tl-selection__fg");
      const bg = document.querySelector(".tl-selection__bg");
      const elapsed = performance.now() - start;
      if (elapsed >= maxMs || (!fg && !bg && elapsed >= minMs)) {
        // Extra frame so the browser paints the removal before export starts.
        requestAnimationFrame(() => resolve());
      } else {
        requestAnimationFrame(check);
      }
    }
    requestAnimationFrame(check);
  });
}

/** Remove any selection-related elements from an SVG string. */
function stripSelectionFromSvg(svg: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svg, "image/svg+xml");
  doc
    .querySelectorAll(
      '.tl-selection__fg, .tl-selection__bg, [class*="tl-selection"], [data-testid="selection-foreground"]'
    )
    .forEach((el) => el.remove());
  return new XMLSerializer().serializeToString(doc.documentElement);
}

/**
 * Custom "send to back" that places selected shapes just above canvas infrastructure shapes,
 * instead of sending them to the absolute bottom of the z-stack.
 * Uses direct index manipulation for reliability.
 */
export function safeSendToBack(editor: Editor) {
  const selectedShapes = editor.getSelectedShapes();
  if (selectedShapes.length === 0) return;

  const selectedIdSet = new Set(selectedShapes.map((s) => s.id as string));
  const pageId = editor.getCurrentPageId();
  const sortedIds = editor.getSortedChildIdsForParent(pageId);

  // Pass 1: find the highest BOTTOM infrastructure shape index (the "floor" for user content)
  // Only check CANVAS_BOTTOM_IDS — NOT the top ones (die lines, labels, etc.)
  let highestBottomInfraIndex: IndexKey | null = null;
  for (const id of sortedIds) {
    if (CANVAS_BOTTOM_IDS.has(id as string)) {
      const shape = editor.getShape(id);
      if (shape) highestBottomInfraIndex = shape.index;
    }
  }

  if (highestBottomInfraIndex === null) {
    // No bottom infrastructure shapes found, just send to back normally
    editor.sendToBack(selectedShapes);
    return;
  }

  // Pass 2: find the lowest non-infra, non-selected shape index that is ABOVE the bottom infra floor
  let firstUserAbove: IndexKey | null = null;
  for (const id of sortedIds) {
    if (isInfrastructureShape(id as string)) continue;
    if (selectedIdSet.has(id as string)) continue;
    const shape = editor.getShape(id);
    if (shape && shape.index > highestBottomInfraIndex) {
      firstUserAbove = shape.index;
      break; // sorted list, so this is the lowest one above
    }
  }

  // Generate new indices just above the highest bottom infrastructure shape
  const upperBound = firstUserAbove ?? getIndexAbove(highestBottomInfraIndex);
  const newIndices = getIndicesBetween(highestBottomInfraIndex, upperBound, selectedShapes.length);

  // Sort selected shapes by their current index to maintain relative order
  const sortedSelected = [...selectedShapes].sort((a, b) => (a.index < b.index ? -1 : a.index > b.index ? 1 : 0));

  editor.updateShapes(
    sortedSelected.map((shape, i) => ({
      id: shape.id,
      type: shape.type,
      index: newIndices[i],
    }))
  );
}

/**
 * Custom "send backward" that moves the shape one step back in z-order,
 * considering ALL shapes (not just overlapping ones like tldraw's default),
 * and preventing shapes from going behind canvas infrastructure shapes.
 *
 * Works by building a "user-only" z-order (filtering out infra shapes),
 * finding the target position in that list, then using the FULL sorted list
 * to calculate correct index bounds for placement.
 */
export function safeSendBackward(editor: Editor) {
  const selectedShapes = editor.getSelectedShapes();
  if (selectedShapes.length === 0) return;

  const selectedIdSet = new Set(selectedShapes.map((s) => s.id as string));
  const pageId = editor.getCurrentPageId();
  const sortedIds = editor.getSortedChildIdsForParent(pageId);

  // Build "user-only" sorted list (no infra, no selected shapes)
  const userOnlyIds = sortedIds.filter(
    (id) => !isInfrastructureShape(id as string) && !selectedIdSet.has(id as string)
  );

  // Sort selected shapes by their current index (lowest first) to process in order
  const sortedSelected = [...selectedShapes].sort((a, b) => (a.index < b.index ? -1 : a.index > b.index ? 1 : 0));

  const updates: { id: TLShapeId; type: string; index: IndexKey }[] = [];

  for (const selected of sortedSelected) {
    // Find where this shape sits relative to user-only shapes
    // Count how many user-only shapes are below this shape
    let userPosBelow = 0;
    for (const uid of userOnlyIds) {
      const uShape = editor.getShape(uid);
      if (uShape && uShape.index < selected.index) {
        userPosBelow++;
      } else {
        break; // sorted list, once we pass the shape we're done
      }
    }

    // If there are no user shapes below, we can't go further back
    if (userPosBelow === 0) continue;

    // The target user shape to move behind is at userPosBelow - 1
    const targetUserShapeId = userOnlyIds[userPosBelow - 1];
    const targetUserShape = editor.getShape(targetUserShapeId);
    if (!targetUserShape) continue;

    // Now find what's directly below the target in the FULL sorted list
    // to get proper bounds for index calculation
    const targetFullIdx = sortedIds.indexOf(targetUserShapeId);
    if (targetFullIdx < 0) continue;

    // Look for the shape just below the target in the full list,
    // skipping selected shapes (which are being moved)
    let lowerBound: IndexKey | undefined = undefined;
    for (let i = targetFullIdx - 1; i >= 0; i--) {
      if (selectedIdSet.has(sortedIds[i] as string)) continue;
      const belowShape = editor.getShape(sortedIds[i]);
      if (belowShape) {
        // If this is an infra shape, use it as the lower bound (don't go below it)
        lowerBound = belowShape.index;
        break;
      }
    }

    // Ensure lowerBound < targetUserShape.index to avoid crash
    if (lowerBound !== undefined && lowerBound >= targetUserShape.index) continue;

    const newIndices = getIndicesBetween(lowerBound as IndexKey, targetUserShape.index, 1);

    updates.push({
      id: selected.id,
      type: selected.type,
      index: newIndices[0],
    });
  }

  if (updates.length > 0) {
    editor.updateShapes(updates);
  }
}

/**
 * Custom "bring forward" that moves the shape one step forward in z-order,
 * considering ALL shapes (not just overlapping ones like tldraw's default).
 *
 * Works by building a "user-only" z-order (filtering out infra shapes),
 * finding the target position in that list, then using the FULL sorted list
 * to calculate correct index bounds for placement.
 */
export function safeBringForward(editor: Editor) {
  const selectedShapes = editor.getSelectedShapes();
  if (selectedShapes.length === 0) return;

  const selectedIdSet = new Set(selectedShapes.map((s) => s.id as string));
  const pageId = editor.getCurrentPageId();
  const sortedIds = editor.getSortedChildIdsForParent(pageId);

  // Build "user-only" sorted list (no infra, no selected shapes)
  const userOnlyIds = sortedIds.filter(
    (id) => !isInfrastructureShape(id as string) && !selectedIdSet.has(id as string)
  );

  // Sort selected shapes by their current index (highest first) to process top-down
  const sortedSelected = [...selectedShapes].sort((a, b) => (a.index < b.index ? 1 : a.index > b.index ? -1 : 0));

  const updates: { id: TLShapeId; type: string; index: IndexKey }[] = [];

  for (const selected of sortedSelected) {
    // Find where this shape sits relative to user-only shapes
    // Count how many user-only shapes are above this shape
    let userPosAboveCount = 0;
    let firstAboveIdx = -1;
    for (let i = 0; i < userOnlyIds.length; i++) {
      const uShape = editor.getShape(userOnlyIds[i]);
      if (uShape && uShape.index > selected.index) {
        if (firstAboveIdx === -1) firstAboveIdx = i;
        userPosAboveCount++;
      }
    }

    // If there are no user shapes above, we're already at the top of user shapes
    if (userPosAboveCount === 0 || firstAboveIdx === -1) continue;

    // The target user shape to move above is the first one above
    const targetUserShapeId = userOnlyIds[firstAboveIdx];
    const targetUserShape = editor.getShape(targetUserShapeId);
    if (!targetUserShape) continue;

    // Now find what's directly above the target in the FULL sorted list
    // to get proper bounds for index calculation
    const targetFullIdx = sortedIds.indexOf(targetUserShapeId);
    if (targetFullIdx < 0) continue;

    // Look for the shape just above the target in the full list,
    // skipping selected shapes (which are being moved)
    let upperBound: IndexKey | undefined = undefined;
    for (let i = targetFullIdx + 1; i < sortedIds.length; i++) {
      if (selectedIdSet.has(sortedIds[i] as string)) continue;
      const aboveShape = editor.getShape(sortedIds[i]);
      if (aboveShape) {
        upperBound = aboveShape.index;
        break;
      }
    }

    // Ensure targetUserShape.index < upperBound to avoid crash
    if (upperBound !== undefined && targetUserShape.index >= upperBound) continue;

    const newIndices = getIndicesBetween(targetUserShape.index, upperBound as IndexKey, 1);

    updates.push({
      id: selected.id,
      type: selected.type,
      index: newIndices[0],
    });
  }

  if (updates.length > 0) {
    editor.updateShapes(updates);
  }
}

// Simple toast notification for copy actions
function showCopyToast(message: string, type: "success" | "error" = "success") {
  // Remove any existing copy toast
  const existing = document.getElementById("copy-toast-notification");
  if (existing) existing.remove();

  const toast = document.createElement("div");
  toast.id = "copy-toast-notification";
  toast.textContent = message;
  const bg = type === "success" ? "#333" : "#d32f2f";
  // BottomToolPanel is at bottom-8 (32px) with ~48px height — place toast just above it
  toast.style.cssText = `
    position: fixed; bottom: 95px; left: 50%; transform: translateX(-50%);
    background: ${bg}; color: #fff; padding: 10px 20px; border-radius: 8px;
    z-index: 100000000001; font-size: 14px; font-family: sans-serif;
    box-shadow: 0 4px 12px rgba(0,0,0,0.25); opacity: 0; transition: opacity 0.25s ease;
    pointer-events: none;
  `;
  document.body.appendChild(toast);
  requestAnimationFrame(() => {
    toast.style.opacity = "1";
  });
  setTimeout(() => {
    toast.style.opacity = "0";
    setTimeout(() => toast.remove(), 300);
  }, 2000);
}

//!hint
// 	shift: !
//  ctrl/cmd: $
// 	alt: ?
export const uiOverrides = {
  actions(editor: any, actions: any, helpers: any): TLUiActionsContextType {
    const origPaste = actions["paste"];
    const origUngroup = actions["ungroup"];

    const origDuplicate = actions["duplicate"];

    const newAction = {
      ...actions,

      // Override duplicate to use 5px offset
      duplicate: {
        ...origDuplicate,
        onSelect: (source: string) => {
          const selectedIds = editor.getSelectedShapeIds();
          if (selectedIds.length === 0) return;
          editor.markHistoryStoppingPoint("duplicate shapes");
          editor.duplicateShapes(selectedIds);
          // After duplicate, tldraw selects the new shapes — nudge them +5px
          const newIds = editor.getSelectedShapeIds();
          newIds.forEach((id: TLShapeId) => {
            const shape = editor.getShape(id);
            if (shape) {
              editor.updateShape({ id, type: shape.type, x: shape.x + 5, y: shape.y + 5 });
            }
          });
        },
      },

      // Create a custom action in the ui's context.
      group: { ...actions["group"], kbd: "$g" },
      ungroup: {
        ...actions["ungroup"],
        kbd: "$!g",
        onSelect: (source: string) => {
          const selectedIds = editor.getSelectedShapeIds();
          if (selectedIds.length === 1) {
            const shape = editor.getShape(selectedIds[0]);
            if (shape?.meta?.isMaskShape) {
              editor.markHistoryStoppingPoint("ungroup");
              ungroupMaskGroup(shape.id as TLShapeId, editor);
              return;
            }
          }
          origUngroup?.onSelect?.(source);
        },
      },
      "insert-embed": { ...actions["insert-embed"], kbd: undefined },
      "bring-to-front": { ...actions["bring-to-front"], kbd: "$!]" },
      "send-to-back": {
        ...actions["send-to-back"],
        kbd: "$![",
        onSelect: () => {
          editor.markHistoryStoppingPoint("send to back");
          safeSendToBack(editor);
        },
      },
      "bring-forward": {
        ...actions["bring-forward"],
        kbd: "$]",
        onSelect: () => {
          editor.markHistoryStoppingPoint("bring forward");
          safeBringForward(editor);
        },
      },
      "send-backward": {
        ...actions["send-backward"],
        kbd: "$[",
        onSelect: () => {
          editor.markHistoryStoppingPoint("send backward");
          safeSendBackward(editor);
        },
      },
      "toggle-dark-mode": { ...actions["toggle-dark-mode"], kbd: undefined },
      // Override copy-as-png/svg to work like Figma:
      // - Transparent background (no canvas fill)
      // - Tight bounds with no extra padding
      // - Deselect first so selection border is not included
      "copy-as-png": {
        ...actions["copy-as-png"],
        onSelect: async () => {
          const selectedIds = [...editor.getSelectedShapeIds()];
          if (selectedIds.length === 0) return;
          editor.selectNone();
          // Build a lazy blob promise — the actual PNG generation happens
          // after the selection UI is gone from the DOM, but clipboard.write()
          // is called synchronously (within the user gesture).
          const blobPromise = (async () => {
            await waitForSelectionGone();
            return exportToBlob({
              editor,
              ids: selectedIds,
              format: "png",
              opts: { background: false, padding: 0 },
            });
          })();
          try {
            await navigator.clipboard.write([new ClipboardItem({ "image/png": blobPromise } as any)]);
            showCopyToast("Copied as PNG");
          } catch (e) {
            console.error("Copy as PNG failed:", e);
            showCopyToast("Failed to copy as PNG", "error");
          }
        },
      },
      "copy-as-svg": {
        ...actions["copy-as-svg"],
        onSelect: async () => {
          const selectedIds = [...editor.getSelectedShapeIds()];
          if (selectedIds.length === 0) return;
          editor.selectNone();
          await waitForSelectionGone();
          try {
            const result = await editor.getSvgString(selectedIds, {
              background: false,
              padding: 0,
            });
            if (result?.svg) {
              const cleanSvg = stripSelectionFromSvg(result.svg);
              await navigator.clipboard.writeText(cleanSvg);
              showCopyToast("Copied as SVG");
            }
          } catch (e) {
            console.error("Copy as SVG failed:", e);
            showCopyToast("Failed to copy as SVG", "error");
          }
        },
      },
      // Override export-as-png/svg to deselect before exporting (same as copy-as)
      "export-as-png": {
        ...actions["export-as-png"],
        onSelect: async (source: string) => {
          const selectedIds = [...editor.getSelectedShapeIds()];
          if (selectedIds.length === 0) return;
          editor.selectNone();
          await waitForSelectionGone();
          try {
            await exportAs(editor, selectedIds, "png", undefined, {
              background: false,
              padding: 0,
            });
          } catch (e) {
            console.error("Export as PNG failed:", e);
          }
        },
      },
      "export-as-svg": {
        ...actions["export-as-svg"],
        onSelect: async (source: string) => {
          const selectedIds = [...editor.getSelectedShapeIds()];
          if (selectedIds.length === 0) return;
          editor.selectNone();
          await waitForSelectionGone();
          try {
            const result = await editor.getSvgString(selectedIds, {
              background: false,
              padding: 0,
            });
            if (result?.svg) {
              const cleanSvg = stripSelectionFromSvg(result.svg);
              const blob = new Blob([cleanSvg], { type: "image/svg+xml" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = "export.svg";
              a.click();
              URL.revokeObjectURL(url);
            }
          } catch (e) {
            console.error("Export as SVG failed:", e);
          }
        },
      },
      // Keep `kbd` set on copy/cut/paste so the context menu renders the
      // shortcut label next to each item (the right-click menu uses
      // `kbd` for the right-aligned hint). For COPY and CUT the
      // tldraw action's default `onSelect` is also bound to the
      // keyboard, but our `useKeyboardShortcuts` hook calls the same
      // `editor.copy()` / `editor.cut()` underneath, so a double-fire
      // is idempotent (copy → clipboard last-write-wins; cut → second
      // try is a no-op since the shapes are already gone).
      copy: { ...actions["copy"], kbd: "$c" },
      cut: { ...actions["cut"], kbd: "$x" },
      // Paste is trickier: tldraw v3 listens to the browser's native
      // `paste` event in `useClipboardEvents`, separate from the
      // action-system keybinding. If we let the action's `onSelect`
      // also fire on Cmd+V we'd run the image/SVG fallback below in
      // ADDITION to the native paste, double-pasting. Solution: keep
      // `kbd: "$v"` for display, but bail out of `onSelect` when the
      // source is "kbd" — the native browser paste handler will run
      // and tldraw's built-in paste does the right thing for text /
      // internal copies. Menu / context-menu clicks still go through
      // the full custom image-or-SVG logic below.
      paste: {
        ...origPaste,
        kbd: "$v",
        onSelect: async (source: string) => {
          if (source === "kbd") return;
          try {
            const nav: any = navigator as any;
            if (typeof nav?.clipboard?.read === "function") {
              const items: any[] = await nav.clipboard.read();

              for (const item of items) {
                // Check for image/* mime types first
                const type: string | undefined = item.types?.find((t: string) => t.startsWith("image/"));
                if (type) {
                  const blob = await item.getType(type);
                  const dataUrl: string = await new Promise((resolve, reject) => {
                    const fr = new FileReader();
                    fr.onload = () => resolve(fr.result as string);
                    fr.onerror = reject;
                    fr.readAsDataURL(blob);
                  });

                  // Measure image to keep aspect ratio
                  const img = window.document.createElement("img");
                  await new Promise<void>((res) => {
                    img.onload = () => res();
                    img.src = dataUrl;
                  });
                  const aspect = img.width && img.height ? img.width / img.height : 1;
                  const imageWidth = 200;
                  const imageHeight = imageWidth / aspect;

                  // Center placement
                  const viewport = editor.getViewportScreenBounds();
                  const center = editor.screenToPage({ x: viewport.width / 2, y: viewport.height / 2 });

                  const assetId = `asset:clipboard-image-${Date.now()}` as any;
                  editor.createAssets([
                    {
                      id: assetId,
                      type: "image",
                      typeName: "asset",
                      props: {
                        name: "pasted-image",
                        src: dataUrl,
                        w: imageWidth,
                        h: imageHeight,
                        mimeType: type,
                        isAnimated: false,
                      },
                      meta: { name: "pasted-image" },
                    },
                  ]);

                  const shapeId = `shape:clipboard-image-${Date.now()}` as any;
                  editor.createShapes([
                    {
                      id: shapeId,
                      type: "customImage",
                      props: { assetId, w: imageWidth, h: imageHeight },
                      x: center.x - imageWidth / 2,
                      y: center.y - imageHeight / 2,
                    },
                  ]);
                  editor.select(shapeId);

                  return; // handled image paste
                }

                // Check for SVG text in clipboard (from copy-as-svg)
                const hasText = item.types?.includes("text/plain");
                if (hasText) {
                  const textBlob = await item.getType("text/plain");
                  const text = await textBlob.text();
                  const trimmed = text.trim();
                  if (trimmed.startsWith("<svg") || trimmed.startsWith("<?xml")) {
                    // Convert SVG text to data URL and paste as image
                    const svgDataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(trimmed)}`;
                    const img = window.document.createElement("img");
                    await new Promise<void>((res, rej) => {
                      img.onload = () => res();
                      img.onerror = () => rej(new Error("SVG load failed"));
                      img.src = svgDataUrl;
                    });
                    const aspect = img.width && img.height ? img.width / img.height : 1;
                    const imageWidth = 200;
                    const imageHeight = imageWidth / aspect;

                    const viewport = editor.getViewportScreenBounds();
                    const center = editor.screenToPage({ x: viewport.width / 2, y: viewport.height / 2 });

                    const assetId = `asset:clipboard-svg-${Date.now()}` as any;
                    editor.createAssets([
                      {
                        id: assetId,
                        type: "image",
                        typeName: "asset",
                        props: {
                          name: "pasted-svg",
                          src: svgDataUrl,
                          w: imageWidth,
                          h: imageHeight,
                          mimeType: "image/svg+xml",
                          isAnimated: false,
                        },
                        meta: { name: "pasted-svg" },
                      },
                    ]);

                    const shapeId = `shape:clipboard-svg-${Date.now()}` as any;
                    editor.createShapes([
                      {
                        id: shapeId,
                        type: "customImage",
                        props: { assetId, w: imageWidth, h: imageHeight },
                        x: center.x - imageWidth / 2,
                        y: center.y - imageHeight / 2,
                      },
                    ]);
                    editor.select(shapeId);

                    return; // handled SVG text paste
                  }
                }
              }
            }
          } catch {
            /* no-op */
          }
          // Fallback to original paste (text/internal)
          if (typeof origPaste?.onSelect === "function") {
            origPaste.onSelect();
          }
        },
      },
    };

    return newAction;
  },

  tools(editor: any, tools: any): TLUiToolsContextType {
    const newTools = {
      ...tools,
      // Create a tool item in the ui's context.
      // scale: {
      //   id: "scale",
      //   icon: "",
      //   label: "Scale",
      //   kbd: "k",
      //   onSelect: () => {
      //     // editor.setCurrentTool("scale");
      //     let isKKeyDown = false;
      //     const handleKeyDown = (event: KeyboardEvent) => {
      //       if ((event.key === "k" || event.key === "K") && !isKKeyDown) {
      //         isKKeyDown = true;
      //         const selectedShapeIds = editor.getSelectedShapeIds();
      //         selectedShapeIds.forEach((id: TLShapeId) => {
      //           const shape = editor.getShape(id);
      //           if (shape && shape.type === "customImage") return;
      //           if (shape) {
      //             editor.updateShape({
      //               ...shape,
      //               props: {
      //                 ...shape.props,
      //                 isScale: true,
      //               },
      //             });
      //           }
      //         });

      //         const canvas = document.querySelector(".tl-canvas") as HTMLElement;
      //         if (canvas) {
      //           canvas.style.setProperty("cursor", `${scaleCursor} 2 0, auto`);
      //         }
      //       }
      //     };

      //     const handleKeyUp = (event: KeyboardEvent) => {
      //       if ((event.key === "k" || event.key === "K") && isKKeyDown) {
      //         isKKeyDown = false;
      //         const selectedShapeIds = editor.getSelectedShapeIds();
      //         selectedShapeIds.forEach((id: any) => {
      //           const shape = editor.getShape(id);
      //           if (shape) {
      //             if (shape && shape.type === "customImage") return;

      //             editor.updateShape({
      //               ...shape,
      //               props: {
      //                 ...shape.props,
      //                 isScale: false,
      //               },
      //             });
      //           }
      //         });

      //         const canvas = document.querySelector(".tl-canvas") as HTMLElement;
      //         if (canvas) {
      //           canvas.style.removeProperty("cursor");
      //         }
      //       }
      //     };

      //     document.addEventListener("keydown", handleKeyDown);
      //     document.addEventListener("keyup", handleKeyUp);

      //     return () => {
      //       document.removeEventListener("keydown", handleKeyDown);
      //       document.removeEventListener("keyup", handleKeyUp);
      //     };
      //   },
      // },
      customCloud: {
        id: "customCloud",
        icon: "geo-cloud",
        label: "Cloud",
        kbd: undefined,
        onSelect: () => {
          editor.setCurrentTool("customCloud");
        },
      },

      customHeart: {
        id: "customHeart",
        icon: "geo-heart",
        label: "Heart",
        kbd: undefined,
        onSelect: () => {
          editor.setCurrentTool("customHeart");
        },
      },

      customEllipse: {
        id: "customEllipse",
        icon: "geo-ellipse",
        label: "Ellipse",
        kbd: "!o",
        onSelect: () => {
          editor.setCurrentTool("customEllipse");
        },
      },

      customOval: {
        id: "customOval",
        icon: "geo-oval",
        label: "Oval",
        kbd: undefined,
        onSelect: () => {
          editor.setCurrentTool("customOval");
        },
      },

      customTriangle: {
        id: "customTriangle",
        icon: "geo-triangle",
        label: "Polygon",
        // ⇧+P selects the polygon tool. This binding migrated here
        // from the (now-disabled) pen-draw shortcut so the key still
        // does something useful in our toolbar — the Shapes flyout
        // surfaces this tool under the "Polygon" label.
        kbd: "!p",
        onSelect: () => {
          editor.setCurrentTool("customTriangle");
        },
      },

      customRect: {
        id: "customRect",
        icon: "geo-rectangle",
        label: "Rectangle",
        kbd: "!r",
        onSelect: () => {
          editor.setCurrentTool("customRect");
        },
      },

      customStar: {
        id: "customStar",
        icon: "geo-star",
        label: "Star",
        kbd: undefined,
        onSelect: () => {
          editor.setCurrentTool("customStar");
        },
      },

      customCheckBox: {
        id: "customCheckBox",
        icon: "geo-check-box",
        label: "Checkbox",
        kbd: undefined,
        onSelect: () => {
          editor.setCurrentTool("customCheckBox");
        },
      },

      customXBox: {
        id: "customXBox",
        icon: "geo-x-box",
        label: "XBox",
        kbd: undefined,
        onSelect: () => {
          editor.setCurrentTool("customXBox");
        },
      },

      customText: {
        id: "customText",
        icon: "tool-text",
        label: "Text",
        kbd: "!t",
        onSelect: () => {
          editor.setCurrentTool("customText");
        },
      },

      customSvg: {
        id: "customSvg",
        icon: "geo-svg",
        label: "SVG",
        kbd: undefined,
        onSelect: () => {
          editor.setCurrentTool("customSvg");
        },
      },
      customDraw: {
        id: "customDraw",
        icon: "tool-pencil",
        label: "Draw",
        // No keyboard shortcut for the pencil tool — `p` and `⇧+P`
        // are both intentionally unbound. The toolbar button is the
        // only way in. To re-enable, set `kbd` here (and remember to
        // also unmute the default `draw` tool below if `p` is the
        // chosen binding).
        kbd: undefined,
        onSelect: () => {
          editor.setCurrentTool("customDraw");
        },
      },

      customHighlight: {
        id: "customHighlight",
        icon: "tool-highlight",
        label: "Highlight",
        kbd: undefined,
        onSelect: () => {
          editor.setCurrentTool("customHighlight");
        },
      },

      text: { ...tools.text, kbd: undefined },
      rectangle: { ...tools.rectangle, kbd: undefined },
      elipse: { ...tools.elipse, kbd: undefined },
      ellipse: { ...tools.arrow, kbd: undefined },
      laser: { ...tools.laser, kbd: undefined },
      // Disable tldraw's default pen-draw tool keyboard shortcut.
      // The plain `p` key was switching the canvas into pen-drawing
      // mode unexpectedly — we don't surface the pen tool in our
      // toolbar (our visible pencil is `customDraw`, bound to ⇧+P),
      // so the binding had no UI affordance and was surprising.
      // Spreading the original tool keeps its onSelect/icon/label
      // intact in case the tool is reachable via other paths; only
      // the kbd is nulled out. Re-enable by restoring `kbd: "p"`
      // when the pen surfaces again as a first-class tool.
      draw: { ...tools.draw, kbd: undefined },
      line: { ...tools.line, kbd: "!l" },
      arrow: { ...tools.arrow, kbd: "!a" },
      select: { ...tools.select, kbd: "v" },
      frame: { ...tools.frame, kbd: "f" },
      zoomIn: { ...tools.zoomIn, kbd: "+" },

      // customDiamond: {
      //   id: "customDiamond",
      //   icon: "geo-diamond",
      //   label: "Diamond",
      //   kbd: "d",
      //   onSelect: () => {
      //     editor.setCurrentTool("customDiamond");
      //   },
      // },

      // customPentagon: {
      //   id: "customPentagon",
      //   icon: "geo-pentagon",
      //   label: "Pentagon",
      //   kbd: "p",
      //   onSelect: () => {
      //     editor.setCurrentTool("customPentagon");
      //   },
      // },

      // customHexagon: {
      //   id: "customHexagon",
      //   icon: "geo-hexagon",
      //   label: "Hexagon",
      //   kbd: "x",
      //   onSelect: () => {
      //     editor.setCurrentTool("customHexagon");
      //   },
      // },

      // customOctagon: {
      //   id: "customOctagon",
      //   icon: "geo-octagon",
      //   label: "Octagon",
      //   kbd: "o",
      //   onSelect: () => {
      //     editor.setCurrentTool("customOctagon");
      //   },
      // },

      // customTrapezoid: {
      //   id: "customTrapezoid",
      //   icon: "geo-trapezoid",
      //   label: "Trapezoid",
      //   kbd: "!r",
      //   onSelect: () => {
      //     editor.setCurrentTool("customTrapezoid");
      //   },
      // },

      // customRhombus: {
      //   id: "customRhombus",
      //   icon: "geo-rhombus",
      //   label: "Rhombus",
      //   kbd: "a",
      //   onSelect: () => {
      //     editor.setCurrentTool("customRhombus");
      //   },
      // },

      // customRhombus2: {
      //   id: "customRhombus2",
      //   icon: "geo-rhombus-2",
      //   label: "Rhombus",
      //   kbd: "",
      //   onSelect: () => {
      //     editor.setCurrentTool("customRhombus2");
      //   },
      // },

      // customArrowRight: {
      //   id: "customArrowRight",
      //   icon: "geo-arrow-right",
      //   label: "Arrow Right",
      //   kbd: "a",
      //   onSelect: () => {
      //     editor.setCurrentTool("customArrowRight");
      //   },
      // },

      // customArrowLeft: {
      //   id: "customArrowLeft",
      //   icon: "geo-arrow-left",
      //   label: "Arrow Left",
      //   kbd: "a",
      //   onSelect: () => {
      //     editor.setCurrentTool("customArrowLeft");
      //   },
      // },

      // customArrowUp: {
      //   id: "customArrowUp",
      //   icon: "geo-arrow-up",
      //   label: "Arrow Up",
      //   kbd: "a",
      //   onSelect: () => {
      //     editor.setCurrentTool("customArrowUp");
      //   },
      // },

      // customArrowDown: {
      //   id: "customArrowDown",
      //   icon: "geo-arrow-down",
      //   label: "Arrow Down",
      //   kbd: "a",
      //   onSelect: () => {
      //     editor.setCurrentTool("customArrowDown");
      //   },
      // },
    };

    return newTools;
  },
};
